import { useCallback } from 'react';
import type { Logger } from '@archipro-website/logger';
import type { Tracker } from '../../types';
import {
    REFERRAL_ATTR_ITEM_ID,
    REFERRAL_ATTR_PROFESSIONAL_ID,
    REFERRAL_ATTR_PROFESSIONAL_TITLE,
} from '../../constants';

/**
 * Return a function that tracks external URL (website referral) clicks, which
 * fires the `Website` event. Only tracks the click if the element is an anchor
 * element and has the correct attributes set (`REFERRAL_ATTR_PROFESSIONAL_ID`,
 * `REFERRAL_ATTR_PROFESSIONAL_TITLE`, `REFERRAL_ATTR_ITEM_ID`).
 * @param trackerInstance The tracker instance.
 * @param onTrack Callback when the `Website` event is tracked.
 */
export const useTrackWebsiteReferralClick = (
    trackerInstance: Tracker,
    logger: Logger,
    onTrack?: (
        url: string,
        data: {
            itemID: number;
            professionalID: number;
            professionalTitle: string;
        },
    ) => void,
) => {
    return useCallback(
        (target: HTMLElement, mouseButton: 0 | 1 | 2) => {
            const professionalIDAttr = target.getAttribute(
                REFERRAL_ATTR_PROFESSIONAL_ID,
            );
            const professionalTitle = target.getAttribute(
                REFERRAL_ATTR_PROFESSIONAL_TITLE,
            );
            const itemIDAttr = target.getAttribute(REFERRAL_ATTR_ITEM_ID);

            if (!professionalIDAttr) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                logger.warning(
                    `Missing attribute: ${REFERRAL_ATTR_PROFESSIONAL_ID}. Tracking performance will be degraded.`,
                );
                return;
            }
            if (!professionalTitle) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                logger.warning(
                    `Missing attribute: ${REFERRAL_ATTR_PROFESSIONAL_TITLE}. Tracking performance will be degraded.`,
                );
                return;
            }
            if (!itemIDAttr) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                logger.warning(
                    `Missing attribute: ${REFERRAL_ATTR_ITEM_ID}. Critical tracking failure.`,
                );
                return;
            }
            if (!(target instanceof HTMLAnchorElement)) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                logger.warning('Target element is not an anchor element');
                return;
            }

            const url = new URL(target.href);
            url.searchParams.delete('_ap_pageid');
            url.searchParams.delete('utm_source');
            url.searchParams.delete('utm_medium');
            url.searchParams.delete('utm_campaign');
            url.searchParams.delete('__apid');
            const externalURL = url.toString();
            const professionalID = parseInt(professionalIDAttr) || 0;
            const itemID = parseInt(itemIDAttr) || 0;

            void trackerInstance.log('Website', {
                url: new URL(window.location.href),
                label: professionalTitle,
                data: {
                    LibraryItemID: itemID,
                    ProfessionalID: professionalID,
                    ExternalURL: externalURL,
                    ExtraData: {
                        MouseButton: mouseButton,
                    },
                },
            });

            onTrack?.(externalURL, {
                itemID,
                professionalID,
                professionalTitle,
            });
        },
        [trackerInstance, logger, onTrack],
    );
};
